import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { Checkbox, Button } from '@chaser/components';
import { Settings, X } from 'lucide-react';
import { AVAILABLE_ROLES, roleNames } from '../../../../util/constants';
import { toast } from '../../../../components/ToastContainer';
import styles from './RoleMultiSelect.module.scss';

const RoleMultiSelect = ({ roles, disabled, disabledRoles = [], onChange }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverClosing, setPopoverClosing] = useState(false);
  const [pending, setPending] = useState(roles);
  const [isLoading, setIsLoading] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, width: 0, above: false });
  const containerRef = useRef(null);
  const popoverRef = useRef(null);
  const cogRef = useRef(null);

  useLayoutEffect(() => {
    if (!popoverOpen || !popoverPos.above || !popoverRef.current || !containerRef.current) return;
    const popoverHeight = popoverRef.current.offsetHeight;
    const rect = containerRef.current.getBoundingClientRect();
    setPopoverPos(prev => ({
      ...prev,
      top: rect.top + window.scrollY - popoverHeight - 4,
    }));
  }, [popoverOpen]);

  useEffect(() => {
    if (!popoverOpen) return;
    const handleClickOutside = e => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        cogRef.current &&
        !cogRef.current.contains(e.target)
      ) {
        closePopover();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popoverOpen, popoverClosing]);

  const closePopover = () => {
    setPopoverClosing(true);
    setTimeout(() => {
      setPopoverOpen(false);
      setPopoverClosing(false);
    }, 150);
  };

  const POPOVER_ESTIMATED_HEIGHT = 320;

  const calcPopoverPos = rect => {
    const spaceBelow = window.innerHeight - rect.bottom;
    const above = spaceBelow < POPOVER_ESTIMATED_HEIGHT && rect.top > POPOVER_ESTIMATED_HEIGHT;
    return {
      top: above
        ? rect.top + window.scrollY - POPOVER_ESTIMATED_HEIGHT - 4
        : rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width,
      above,
    };
  };

  const updatePopoverPos = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setPopoverPos(calcPopoverPos(rect));
  };

  useEffect(() => {
    if (!popoverOpen) return;
    window.addEventListener('resize', updatePopoverPos);
    window.addEventListener('scroll', updatePopoverPos, true);
    return () => {
      window.removeEventListener('resize', updatePopoverPos);
      window.removeEventListener('scroll', updatePopoverPos, true);
    };
  }, [popoverOpen]);

  useEffect(() => {
    setIsLoading(false);
  }, [roles]);

  const openPopover = () => {
    if (disabled || isLoading) return;
    if (popoverOpen) {
      closePopover();
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    setPopoverPos(calcPopoverPos(rect));
    setPending([...roles]);
    setPopoverOpen(true);
  };

  const handleApply = () => {
    closePopover();
    if ([...pending].sort().join(',') !== [...roles].sort().join(',')) {
      setIsLoading(true);
      onChange(pending);
    }
  };

  const isDisabled = disabled || isLoading;

  const toggleRole = role => {
    if (pending.includes(role) && pending.length === 1) {
      toast.info('A user must have at least one role');
      return;
    }
    setPending(prev => (prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]));
  };

  const removeRole = (e, role) => {
    e.stopPropagation();
    if (isDisabled) return;
    const newRoles = roles.filter(r => r !== role);
    if (newRoles.length === 0) {
      toast.info('A user must have at least one role');
      return;
    }
    setIsLoading(true);
    onChange(newRoles);
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${isDisabled ? styles.containerDisabled : ''}`}
    >
      <div
        className={styles.tags}
        onClick={openPopover}
        style={{ cursor: isDisabled ? 'default' : 'pointer' }}
      >
        {roles.length === 0 ? (
          <span className={styles.placeholder}>No roles</span>
        ) : (
          roles.map(role => (
            <span key={role} className={styles.roleTag}>
              {roleNames[role]}
              <button
                type="button"
                className={styles.roleTagRemove}
                onClick={e => removeRole(e, role)}
                aria-label={`Remove ${roleNames[role]}`}
              >
                <X size={11} />
              </button>
            </span>
          ))
        )}
      </div>
      <button
        ref={cogRef}
        type="button"
        className={`${styles.cogButton} ${isDisabled ? styles.cogDisabled : ''}`}
        onClick={openPopover}
        disabled={isDisabled}
        aria-label="Edit roles"
      >
        <Settings size={16} />
      </button>
      {popoverOpen &&
        ReactDOM.createPortal(
          <div
            ref={popoverRef}
            className={`${styles.popover} ${popoverClosing ? styles.popoverClosing : ''} ${
              popoverPos.above ? styles.popoverAbove : ''
            }`}
            style={{ top: popoverPos.top, left: popoverPos.left, width: popoverPos.width }}
          >
            <div className={styles.popoverList}>
              <p className={styles.popoverHint}>Select 1 or more roles</p>
              {AVAILABLE_ROLES.map(role => (
                <Checkbox
                  key={role}
                  checked={pending.includes(role)}
                  disabled={disabledRoles.includes(role) && !roles.includes(role)}
                  onChange={() => toggleRole(role)}
                >
                  {roleNames[role]}
                </Checkbox>
              ))}
            </div>
            <div className={styles.popoverFooter}>
              <Button
                size="narrow"
                onClick={handleApply}
                disabled={
                  pending.length === 0 ||
                  [...pending].sort().join(',') === [...roles].sort().join(',')
                }
              >
                Apply
              </Button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default RoleMultiSelect;
